import { Component, input, output } from "@angular/core";
import { ButtonModule } from "primeng/button";

export type ButtonSeverity =
  | "success"
  | "info"
  | "warn"
  | "danger"
  | "help"
  | "primary"
  | "secondary"
  | "contrast";

export type ButtonIconPosition = "left" | "right" | "top" | "bottom";

export interface HorizontalButton {
  /** Text shown on the button. */
  label: string;
  /** PrimeIcons class(es), e.g. `pi pi-send`. */
  iconPos?: ButtonIconPosition;
  /** Semantic color. */
  severity?: ButtonSeverity;
  /** Click handler for this button (the "method to implement"). */
  action?: () => void;
  /** Stable identifier emitted alongside `buttonClick`. */
  value?: string | number;
  disabled?: boolean;
  outlined?: boolean;
  loading?: boolean;
  text?: boolean;
  /** Extra style class(es), e.g. `bqa-danger-btn`. */
  styleClass?: string;
  /** Icon class(es) rendered in place of / alongside the label, e.g. `bqa-answer-icon bqa-answer-icon-check`. */
  icon?: string;
  /** Screen-reader label, required when the button only renders an icon. */
  ariaLabel?: string;
  testId?: string;
}

/**
 * Standardized horizontal row of equal-width buttons.
 *
 * Behaves the same as `app-vertical-button-group` but lays the buttons out
 * side by side (e.g. a `Cancel` / `Add New Item` footer). The caller specifies
 * the buttons via the `buttons` array where each entry declares:
 *   1. how many buttons (array length),
 *   2. its `severity`, and
 *   3. its `action` (the click method to run).
 */
@Component({
  selector: "app-horizontal-button-group",
  standalone: true,
  imports: [ButtonModule],
  templateUrl: "./horizontal-button-group.component.html",
  styleUrl: "./horizontal-button-group.component.scss"
})
export class HorizontalButtonGroupComponent {
  /** Buttons rendered left to right; the array length sets the count. */
  buttons = input<HorizontalButton[]>([]);
  /** Render each button full width within its cell (default). */
  fullWidth = input<boolean>(true);
  /** Outline the group in the danger color, e.g. when a required choice is missing. */
  invalid = input<boolean>(false);
  testId = input<string>("app-horizontal-button-group");

  /** Emitted with the clicked button (in addition to running its `action`). */
  readonly buttonClick = output<HorizontalButton>();

  protected resolveStyleClass(button: HorizontalButton): string {
    const classes = [button.styleClass ?? ""];
    if (this.fullWidth()) {
      classes.push("p-fullwidth-button");
    }
    return classes.join(" ").trim();
  }

  protected handleClick(button: HorizontalButton): void {
    if (button.disabled) {
      return;
    }
    button.action?.();
    this.buttonClick.emit(button);
  }
}
